import React, { useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
    ArrowDown,
    ArrowLeft,
    ArrowRight,
    ArrowUp,
    ArrowUpRight,
    Lasso,
    Maximize2,
    MousePointer2,
    Pencil,
    Type as TypeIcon,
    X,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { CsAsset } from '../../../../common/models/content-studio.model';
import { AspectRatioControl } from '../canvas/AspectRatioControl';

type ToolId = 'expand' | 'retouch' | 'modify' | 'graphics';

interface ImageEditorModalProps {
    open: boolean;
    asset: CsAsset | null;
    /** Which tool's pane to show; if omitted, defaults to expand. */
    initialTool?: ToolId;
    /** Pre-fill the Expand tool with previously-saved values. */
    initialExpand?: ExpandParams;
    /** Pre-fill the Retouch tool with previously-saved regions. */
    initialRetouch?: RetouchParams;
    /** Pre-fill the Modify tool with a previously-saved prompt. */
    initialModify?: ModifyParams;
    /** Pre-fill the Graphics tool with previously-saved annotations. */
    initialGraphics?: GraphicsParams;
    onClose: () => void;
    /** Persist Expand settings back to the edit-image canvas node. */
    onSaveExpand: (params: ExpandParams) => void;
    /** Persist Retouch settings back to the edit-image canvas node. */
    onSaveRetouch: (params: RetouchParams) => void;
    /** Persist Modify settings back to the edit-image canvas node. */
    onSaveModify: (params: ModifyParams) => void;
    /** Persist Graphics settings back to the edit-image canvas node. */
    onSaveGraphics: (params: GraphicsParams) => void;
    /**
     * Upload a reference image file — used by the Modify and Graphics
     * tools' optional reference slots. Returns the uploaded URL.
     */
    onUploadReferenceImage?: (file: File) => Promise<string>;
    expandCreditCost: number;
    retouchCreditCost: number;
    modifyCreditCost: number;
    graphicsCreditCost: number;
    /** The confirm button's words. The canvas saves settings onto a node; the Studio runs the edit at once. */
    saveLabel?: string;
}

export interface ExpandParams {
    extendTop: number;
    extendRight: number;
    extendBottom: number;
    extendLeft: number;
    prompt: string;
}

export interface RetouchPoint { x: number; y: number }

export interface RetouchRegionParams {
    id: string;
    /** Lasso polygon in SOURCE-image pixel coords (>= 3 points). */
    points: RetouchPoint[];
    /** Derived bounding box (source px) — kept for the worker's composite
     *  placement and back-compat with the old rectangle regions. */
    x: number;
    y: number;
    width: number;
    height: number;
    prompt: string;
}

/** Axis-aligned bounding box of a polygon (source px). */
export function retouchBBox(points: RetouchPoint[]): { x: number; y: number; width: number; height: number } {
    if (!points || points.length === 0) return { x: 0, y: 0, width: 0, height: 0 };
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const p of points) {
        if (p.x < minX) minX = p.x;
        if (p.y < minY) minY = p.y;
        if (p.x > maxX) maxX = p.x;
        if (p.y > maxY) maxY = p.y;
    }
    return { x: Math.round(minX), y: Math.round(minY), width: Math.round(maxX - minX), height: Math.round(maxY - minY) };
}

/** Legacy rect regions (no polygon) → a 4-corner polygon so old saves render. */
export function normalizeRetouchRegion(r: RetouchRegionParams): RetouchRegionParams {
    if (r.points && r.points.length >= 3) return r;
    const pts: RetouchPoint[] = [
        { x: r.x, y: r.y },
        { x: r.x + r.width, y: r.y },
        { x: r.x + r.width, y: r.y + r.height },
        { x: r.x, y: r.y + r.height },
    ];
    return { ...r, points: pts };
}

export interface RetouchParams {
    regions: RetouchRegionParams[];
}

export interface ModifyParams {
    prompt: string;
    /**
     * Optional reference image URL. Uploaded via the modal's
     * onUploadReferenceImage callback; shipped to the worker as a
     * second image_urls entry so Flux can match a visual target
     * ("add a side table like this").
     */
    referenceUrl?: string;
}

/** A text overlay placed on the Graphics-tool canvas. Coords are in the
 * source image's native pixel space so the layout survives scaling. */
export interface GraphicsTextAnnotation {
    id: string;
    text: string;
    x: number;
    y: number;
    fontSize: 'sm' | 'md' | 'lg' | 'xl';
    weight: 'regular' | 'bold';
}

/** A straight arrow annotation — endpoints are in source-image pixels. */
export interface GraphicsArrowAnnotation {
    id: string;
    x1: number;
    y1: number;
    x2: number;
    y2: number;
}

/** Graphics tool — place titles, captions, and arrows on the image, then
 * let GPT-Image-2 render a polished marketing graphic using these as a
 * layout sketch (plus an optional style-reference image and freeform
 * prompt). */
export interface GraphicsParams {
    textAnnotations: GraphicsTextAnnotation[];
    arrows: GraphicsArrowAnnotation[];
    /** Optional style-reference image — typography/palette anchor. */
    referenceUrl?: string;
    /** Freeform style guidance (e.g. "cream paper texture background"). */
    prompt: string;
}

/**
 * Graphics is not offered. Design replaced it — the richer text/logo canvas
 * reached from a generation's Edit menu — and `graphics-image` is marked
 * `retired` in the recipe seed ("WITHDRAWN — Design replaced it"). The tab
 * outlived the withdrawal, so the obsolete tool stayed one click away while
 * the recipe behind it was already retired everywhere else.
 *
 * The type, the pipeline and this file's graphics panel stay, so canvases that
 * already carry a graphics node keep rendering and re-running.
 */
const TOOL_IDS: ToolId[] = ['expand', 'retouch', 'modify'];

const TAB_ICONS: Record<ToolId, React.ReactNode> = {
    expand: <Maximize2 />,
    retouch: <Lasso />,
    modify: <Pencil />,
    graphics: <TypeIcon />,
};

/** Uppercase group heading used throughout the tool rails. */
const RailLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div className="text-[11px] font-medium tracking-[0.07em] text-muted-foreground uppercase">{children}</div>
);

/**
 * Optional reference-image slot — a dashed drop target that becomes a
 * preview with a remove button. Modify and Graphics both offer one.
 */
const ReferenceImageSlot: React.FC<{
    url?: string;
    onClear: () => void;
    onPick: () => void;
    uploading: boolean;
    enabled: boolean;
    className?: string;
}> = ({ url, onClear, onPick, uploading, enabled, className }) => {
    const { t } = useTranslation();
    if (url) {
        return (
            <div
                className={cn('relative h-[110px] rounded-lg border border-border bg-muted bg-cover bg-center', className)}
                style={{ backgroundImage: `url(${url})` }}
            >
                <Button
                    type="button"
                    size="icon-xs"
                    variant="ghost"
                    aria-label={t('CONTENT_STUDIO.ACTION_REMOVE')}
                    onClick={(e) => { e.stopPropagation(); onClear(); }}
                    className="absolute end-1.5 top-1.5 bg-black/55 text-white hover:bg-destructive/85 hover:text-white"
                >
                    <X />
                </Button>
            </div>
        );
    }
    return (
        <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onPick(); }}
            className={cn(
                'flex h-[110px] flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-border bg-muted text-xs text-muted-foreground transition-colors',
                enabled ? 'cursor-pointer hover:border-foreground/30 hover:bg-accent' : 'cursor-not-allowed',
                className,
            )}
        >
            {uploading ? t('CONTENT_STUDIO.MODIFY_UPLOADING') : t('CONTENT_STUDIO.MODIFY_UPLOAD_CTA')}
        </button>
    );
};

// Max display size for the interactive preview. The full (source + expansion)
// canvas is scaled to fit so the image stays responsive. On smaller
// laptops (13" MacBook Air ≈ 900px effective height) the full 560
// would push the modal past the viewport — see usePreviewMax below.
const PREVIEW_MAX = 720;

/**
 * Responsive preview-canvas size. The modal chrome (title bar, footer,
 * sidebar, card paddings, toolbars, sliders, prompt row, etc.) eats
 * ~340px of vertical space on the Graphics / Expand tools — so the
 * preview box itself can only take the remaining height. We clamp to a
 * floor of 280px so the canvas stays usable even on very short windows.
 */
function usePreviewMax(): number {
    const getMax = () => {
        if (typeof window === 'undefined') return PREVIEW_MAX;
        // Slim header (tabs only) + footer + card paddings ≈ 300px of chrome.
        const available = window.innerHeight - 300;
        return Math.max(280, Math.min(PREVIEW_MAX, available));
    };
    const [max, setMax] = React.useState(getMax);
    React.useEffect(() => {
        const onResize = () => setMax(getMax());
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);
    return max;
}

// bria/expand cap on the worker side. We clamp the sliders so the user
// can't request a canvas the server will reject.
const MAX_CANVAS_SIDE = 2500;

export const ImageEditorModal: React.FC<ImageEditorModalProps> = ({
    saveLabel,
    open,
    asset,
    initialTool,
    initialExpand,
    initialRetouch,
    initialModify,
    initialGraphics,
    onClose,
    onSaveExpand,
    onSaveRetouch,
    onSaveModify,
    onSaveGraphics,
    onUploadReferenceImage,
    expandCreditCost,
    retouchCreditCost,
    modifyCreditCost,
    graphicsCreditCost,
}) => {
    const { t } = useTranslation();
    const [activeTool, setActiveTool] = useState<ToolId>(initialTool || 'expand');
    const TOOLS: Array<{ id: ToolId; label: string; hint: string }> = TOOL_IDS.map((id) => ({
        id,
        // Short, one-word labels for the compact tab bar.
        label: id === 'expand' ? t('CONTENT_STUDIO.EDIT_TOOL_EXPAND_SHORT')
            : id === 'retouch' ? t('CONTENT_STUDIO.EDIT_TOOL_RETOUCH_SHORT')
            : id === 'modify' ? t('CONTENT_STUDIO.EDIT_TOOL_MODIFY_SHORT')
            : t('CONTENT_STUDIO.EDIT_TOOL_GRAPHICS_SHORT'),
        hint: id === 'expand' ? t('CONTENT_STUDIO.EDIT_TOOL_EXPAND_HINT')
            : id === 'retouch' ? t('CONTENT_STUDIO.EDIT_TOOL_RETOUCH_HINT')
            : id === 'modify' ? t('CONTENT_STUDIO.EDIT_TOOL_MODIFY_HINT')
            : t('CONTENT_STUDIO.EDIT_TOOL_GRAPHICS_HINT'),
    }));
    // Graphics is legacy (superseded by Design) — only surface its tab when a
    // legacy graphics node is actually open on it.
    const visibleTools = TOOLS.filter((tl) => tl.id !== 'graphics' || activeTool === 'graphics');

    // Expand tool state — pixel deltas in the source image's coordinate space.
    const [extendTop, setExtendTop] = useState(initialExpand?.extendTop ?? 0);
    const [extendRight, setExtendRight] = useState(initialExpand?.extendRight ?? 0);
    const [extendBottom, setExtendBottom] = useState(initialExpand?.extendBottom ?? 0);
    const [extendLeft, setExtendLeft] = useState(initialExpand?.extendLeft ?? 0);
    const [promptText, setPromptText] = useState(initialExpand?.prompt ?? '');

    // Retouch tool state — array of lasso regions (polygon + prompt).
    const [regions, setRegions] = useState<RetouchRegionParams[]>(
        (initialRetouch?.regions ?? []).map(normalizeRetouchRegion),
    );
    const [selectedRegionId, setSelectedRegionId] = useState<string | null>(null);

    // Modify tool state — single freeform prompt + optional reference URL.
    const [modifyPrompt, setModifyPrompt] = useState(initialModify?.prompt ?? '');
    const [modifyReferenceUrl, setModifyReferenceUrl] = useState<string | undefined>(
        initialModify?.referenceUrl,
    );

    // Graphics tool state — text boxes, arrows, optional style reference, prompt.
    const [graphicsTexts, setGraphicsTexts] = useState<GraphicsTextAnnotation[]>(
        initialGraphics?.textAnnotations ?? [],
    );
    const [graphicsArrows, setGraphicsArrows] = useState<GraphicsArrowAnnotation[]>(
        initialGraphics?.arrows ?? [],
    );
    const [graphicsReferenceUrl, setGraphicsReferenceUrl] = useState<string | undefined>(
        initialGraphics?.referenceUrl,
    );
    const [graphicsPrompt, setGraphicsPrompt] = useState(initialGraphics?.prompt ?? '');

    // Resync local state whenever the modal re-opens on a different asset
    // or with different persisted settings.
    const lastAssetUuid = useRef<string | null>(null);
    React.useEffect(() => {
        if (!open) return;
        const assetChanged = asset && asset.uuid !== lastAssetUuid.current;
        if (assetChanged) lastAssetUuid.current = asset.uuid;
        setActiveTool(initialTool || 'expand');
        setExtendTop(initialExpand?.extendTop ?? 0);
        setExtendRight(initialExpand?.extendRight ?? 0);
        setExtendBottom(initialExpand?.extendBottom ?? 0);
        setExtendLeft(initialExpand?.extendLeft ?? 0);
        setPromptText(initialExpand?.prompt ?? '');
        setRegions((initialRetouch?.regions ?? []).map(normalizeRetouchRegion));
        setSelectedRegionId(null);
        setModifyPrompt(initialModify?.prompt ?? '');
        setModifyReferenceUrl(initialModify?.referenceUrl);
        setGraphicsTexts(initialGraphics?.textAnnotations ?? []);
        setGraphicsArrows(initialGraphics?.arrows ?? []);
        setGraphicsReferenceUrl(initialGraphics?.referenceUrl);
        setGraphicsPrompt(initialGraphics?.prompt ?? '');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, asset?.uuid]);

    const srcW = asset?.dimensions?.width || 1024;
    const srcH = asset?.dimensions?.height || 1024;
    const canvasW = srcW + extendLeft + extendRight;
    const canvasH = srcH + extendTop + extendBottom;

    // Size the dialog to the IMAGE aspect so a portrait (9:16) doesn't leave big
    // empty side margins. The canvas takes ~previewMax height; the dialog width
    // follows the image aspect + horizontal chrome, floored so the tab bar /
    // footer still fit and capped to the viewport.
    const previewMax = usePreviewMax();
    const paperWidth = useMemo(() => {
        if (typeof window === 'undefined') return 1100;
        const availW = window.innerWidth * 0.94;
        const canvasDisplayW = previewMax * (srcW / srcH);
        return Math.max(560, Math.min(1320, availW, canvasDisplayW + 96));
    }, [previewMax, srcW, srcH]);

    // How much pixel room we have per side before hitting the canvas cap
    // (distribute across the two opposing extends by treating one side at a time).
    const maxExtendW = Math.max(0, MAX_CANVAS_SIDE - srcW);
    const maxExtendH = Math.max(0, MAX_CANVAS_SIDE - srcH);

    const readyRegions = regions.filter(
        (r) => r.prompt.trim() && r.points && r.points.length >= 3 && r.width > 0 && r.height > 0,
    );

    const trimmedModifyPrompt = modifyPrompt.trim();
    const trimmedGraphicsPrompt = graphicsPrompt.trim();
    // Graphics is valid when there's at least one annotation OR a
    // reference image with a prompt — gives the model something to work
    // from. Empty-text annotations are dropped at save time.
    const readyGraphicsTexts = graphicsTexts.filter((a) => a.text.trim().length > 0);
    const hasGraphicsContent =
        readyGraphicsTexts.length > 0
        || graphicsArrows.length > 0
        || (!!graphicsReferenceUrl && trimmedGraphicsPrompt.length > 0);

    const canSave = activeTool === 'expand'
        ? (extendTop + extendRight + extendBottom + extendLeft) > 0
            && canvasW <= MAX_CANVAS_SIDE
            && canvasH <= MAX_CANVAS_SIDE
        : activeTool === 'retouch'
        ? readyRegions.length > 0
        : activeTool === 'modify'
        ? trimmedModifyPrompt.length > 0
        : /* graphics */ hasGraphicsContent;

    const handleSave = () => {
        if (!canSave) return;
        if (activeTool === 'expand') {
            onSaveExpand({
                extendTop,
                extendRight,
                extendBottom,
                extendLeft,
                prompt: promptText.trim(),
            });
        } else if (activeTool === 'retouch') {
            // Persist only ready regions (valid rect + non-empty prompt).
            onSaveRetouch({ regions: readyRegions.map((r) => ({ ...r, prompt: r.prompt.trim() })) });
        } else if (activeTool === 'modify') {
            onSaveModify({
                prompt: trimmedModifyPrompt,
                referenceUrl: modifyReferenceUrl || undefined,
            });
        } else {
            onSaveGraphics({
                textAnnotations: readyGraphicsTexts.map((a) => ({ ...a, text: a.text.trim() })),
                arrows: graphicsArrows,
                referenceUrl: graphicsReferenceUrl || undefined,
                prompt: trimmedGraphicsPrompt,
            });
        }
        onClose();
    };

    return (
        <Dialog open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
            <DialogContent
                showCloseButton={false}
                className="flex max-h-[94vh] max-w-[94vw] flex-col gap-0 overflow-hidden bg-background p-0 sm:max-w-[94vw]"
                // Width adapts to the image aspect (see paperWidth) so portrait
                // images don't leave huge side margins.
                style={{ width: paperWidth }}
            >
                <DialogTitle className="sr-only">{t('CONTENT_STUDIO.NODE_EDIT_IMAGE')}</DialogTitle>

            {/* Header — compact tool tabs + close (no title / no sidebar, so the
                canvas gets the room). */}
            <div className="relative flex items-center justify-center border-b px-4 py-2.5">
                <Tabs value={activeTool} onValueChange={(v) => setActiveTool(v as ToolId)}>
                    <TabsList>
                        {visibleTools.map((tool) => (
                            <TabsTrigger key={tool.id} value={tool.id} title={tool.hint} className="px-3">
                                {TAB_ICONS[tool.id]}
                                {tool.label}
                            </TabsTrigger>
                        ))}
                    </TabsList>
                </Tabs>
                <Button
                    type="button"
                    size="icon-sm"
                    variant="ghost"
                    onClick={onClose}
                    aria-label={t('CONTENT_STUDIO.CANCEL')}
                    className="absolute end-3 top-1/2 -translate-y-1/2"
                >
                    <X />
                </Button>
            </div>

            {/* Body — just the active tool pane (full width). Scrolls internally
                if the tool renders more than fits. */}
            <div className="flex min-h-0 flex-1 overflow-hidden">
                <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-auto p-4">
                    {activeTool === 'expand' && asset && (
                        <ExpandTool
                            asset={asset}
                            srcW={srcW}
                            srcH={srcH}
                            extendTop={extendTop}
                            extendRight={extendRight}
                            extendBottom={extendBottom}
                            extendLeft={extendLeft}
                            maxExtendW={maxExtendW}
                            maxExtendH={maxExtendH}
                            setExtendTop={setExtendTop}
                            setExtendRight={setExtendRight}
                            setExtendBottom={setExtendBottom}
                            setExtendLeft={setExtendLeft}
                            promptText={promptText}
                            setPromptText={setPromptText}
                        />
                    )}
                    {activeTool === 'retouch' && asset && (
                        <RetouchTool
                            asset={asset}
                            srcW={srcW}
                            srcH={srcH}
                            regions={regions}
                            setRegions={setRegions}
                            selectedRegionId={selectedRegionId}
                            setSelectedRegionId={setSelectedRegionId}
                        />
                    )}
                    {activeTool === 'modify' && asset && (
                        <ModifyTool
                            asset={asset}
                            prompt={modifyPrompt}
                            setPrompt={setModifyPrompt}
                            referenceUrl={modifyReferenceUrl}
                            setReferenceUrl={setModifyReferenceUrl}
                            onUploadReference={onUploadReferenceImage}
                        />
                    )}
                    {activeTool === 'graphics' && asset && (
                        <GraphicsTool
                            asset={asset}
                            texts={graphicsTexts}
                            setTexts={setGraphicsTexts}
                            arrows={graphicsArrows}
                            setArrows={setGraphicsArrows}
                            referenceUrl={graphicsReferenceUrl}
                            setReferenceUrl={setGraphicsReferenceUrl}
                            prompt={graphicsPrompt}
                            setPrompt={setGraphicsPrompt}
                            onUploadReference={onUploadReferenceImage}
                        />
                    )}
                </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between border-t bg-muted px-6 py-4">
                <div className="text-xs text-muted-foreground">
                    {activeTool === 'expand' && (
                        <>
                            {t('CONTENT_STUDIO.EDITOR_NEW_CANVAS')}: <strong>{canvasW} × {canvasH}</strong>
                            {' · '}
                            {t('CONTENT_STUDIO.EDITOR_GENERATE_COST')}: <strong>{expandCreditCost} cr</strong>
                        </>
                    )}
                    {activeTool === 'retouch' && (
                        <>
                            {t('CONTENT_STUDIO.EDITOR_REGIONS')}: <strong>{readyRegions.length}</strong>
                            {' · '}
                            {t('CONTENT_STUDIO.EDITOR_GENERATE_COST')}: <strong>{retouchCreditCost} cr</strong>
                        </>
                    )}
                    {activeTool === 'modify' && (
                        <>
                            {t('CONTENT_STUDIO.EDITOR_PROMPT')}: <strong>{
                                trimmedModifyPrompt
                                    ? t('CONTENT_STUDIO.EDITOR_PROMPT_CHARS', { count: trimmedModifyPrompt.length })
                                    : t('CONTENT_STUDIO.EDITOR_PROMPT_EMPTY')
                            }</strong>
                            {' · '}
                            {t('CONTENT_STUDIO.EDITOR_GENERATE_COST')}: <strong>{modifyCreditCost} cr</strong>
                        </>
                    )}
                    {activeTool === 'graphics' && (
                        <>
                            {t('CONTENT_STUDIO.GRAPHICS_TEXTS')}: <strong>{readyGraphicsTexts.length}</strong>
                            {' · '}
                            {t('CONTENT_STUDIO.GRAPHICS_ARROWS')}: <strong>{graphicsArrows.length}</strong>
                            {' · '}
                            {t('CONTENT_STUDIO.EDITOR_GENERATE_COST')}: <strong>{graphicsCreditCost} cr</strong>
                        </>
                    )}
                </div>
                <div className="flex gap-2">
                    <Button type="button" variant="outline" size="lg" onClick={onClose}>
                        {t('CONTENT_STUDIO.CANCEL')}
                    </Button>
                    <Button type="button" size="lg" onClick={handleSave} disabled={!canSave} className="min-w-[120px]">
                        {saveLabel || t('CONTENT_STUDIO.EDITOR_SAVE')}
                    </Button>
                </div>
            </div>
            </DialogContent>
        </Dialog>
    );
};

// ═══════════════════════════════════════════════════════════════════════
// Expand tool — preview with draggable edge handles + numeric controls
// ═══════════════════════════════════════════════════════════════════════

interface ExpandToolProps {
    asset: CsAsset;
    srcW: number;
    srcH: number;
    extendTop: number;
    extendRight: number;
    extendBottom: number;
    extendLeft: number;
    maxExtendW: number;
    maxExtendH: number;
    setExtendTop: (v: number) => void;
    setExtendRight: (v: number) => void;
    setExtendBottom: (v: number) => void;
    setExtendLeft: (v: number) => void;
    promptText: string;
    setPromptText: (v: string) => void;
}

type DragSide = 'top' | 'right' | 'bottom' | 'left';

const ExpandTool: React.FC<ExpandToolProps> = ({
    asset,
    srcW,
    srcH,
    extendTop,
    extendRight,
    extendBottom,
    extendLeft,
    maxExtendW,
    maxExtendH,
    setExtendTop,
    setExtendRight,
    setExtendBottom,
    setExtendLeft,
    promptText,
    setPromptText,
}) => {
    const { t } = useTranslation();
    const previewMax = usePreviewMax();

    const canvasW = srcW + extendLeft + extendRight;
    const canvasH = srcH + extendTop + extendBottom;

    // Scale factor used for on-screen preview — the full expanded canvas
    // must fit inside previewMax on both axes.
    const displayScale = useMemo(() => {
        return Math.min(previewMax / canvasW, previewMax / canvasH, 1);
    }, [canvasW, canvasH, previewMax]);

    const dCanvasW = canvasW * displayScale;
    const dCanvasH = canvasH * displayScale;
    const dSrcW = srcW * displayScale;
    const dSrcH = srcH * displayScale;
    const dOffsetX = extendLeft * displayScale;
    const dOffsetY = extendTop * displayScale;

    const totalX = extendLeft + extendRight;
    const totalY = extendTop + extendBottom;
    const canRepositionImage = totalX > 0 || totalY > 0;

    // Edge-drag accounting. Captures starting value + cursor position on
    // mousedown, computes delta against current cursor, converts screen pixels
    // to source pixels via 1/displayScale.
    const dragRef = useRef<
        | { side: DragSide; startPx: number; startValue: number }
        | null
    >(null);

    const beginDrag = (side: DragSide, e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        const startValue =
            side === 'top' ? extendTop
            : side === 'right' ? extendRight
            : side === 'bottom' ? extendBottom
            : extendLeft;
        const startPx = side === 'left' || side === 'right' ? e.clientX : e.clientY;
        dragRef.current = { side, startPx, startValue };

        const onMove = (ev: MouseEvent) => {
            if (!dragRef.current) return;
            const { side, startPx, startValue } = dragRef.current;
            const currentPx = side === 'left' || side === 'right' ? ev.clientX : ev.clientY;
            let rawDelta = currentPx - startPx; // screen px
            // Invert direction so dragging OUTWARD from the image grows the extension.
            if (side === 'top' || side === 'left') rawDelta = -rawDelta;
            const imagePxDelta = Math.round(rawDelta / (displayScale || 1));
            const nextValue = Math.max(0, startValue + imagePxDelta);

            if (side === 'top') setExtendTop(clampToMax(nextValue, maxExtendH - extendBottom));
            else if (side === 'bottom') setExtendBottom(clampToMax(nextValue, maxExtendH - extendTop));
            else if (side === 'left') setExtendLeft(clampToMax(nextValue, maxExtendW - extendRight));
            else setExtendRight(clampToMax(nextValue, maxExtendW - extendLeft));
        };
        const onUp = () => {
            dragRef.current = null;
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
    };

    // Image-reposition drag: move the source image inside the expanded canvas,
    // redistributing left↔right and top↔bottom extents while keeping the
    // totals (canvas size) constant.
    const beginImageDrag = (e: React.MouseEvent) => {
        if (!canRepositionImage) return;
        e.preventDefault();
        e.stopPropagation();
        const startClientX = e.clientX;
        const startClientY = e.clientY;
        const startLeft = extendLeft;
        const startTop = extendTop;
        const tX = totalX;
        const tY = totalY;

        const onMove = (ev: MouseEvent) => {
            const dx = Math.round((ev.clientX - startClientX) / (displayScale || 1));
            const dy = Math.round((ev.clientY - startClientY) / (displayScale || 1));
            if (tX > 0) {
                const nextLeft = Math.max(0, Math.min(tX, startLeft + dx));
                setExtendLeft(nextLeft);
                setExtendRight(tX - nextLeft);
            }
            if (tY > 0) {
                const nextTop = Math.max(0, Math.min(tY, startTop + dy));
                setExtendTop(nextTop);
                setExtendBottom(tY - nextTop);
            }
        };
        const onUp = () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
    };

    const applyAspectPreset = (ratio: string) => {
        const [w, h] = ratio.split(':').map(Number);
        if (!w || !h) return;
        const targetRatio = w / h;
        const srcRatio = srcW / srcH;
        if (Math.abs(srcRatio - targetRatio) < 0.01) return;
        // Grow along whichever axis needs more room to hit the ratio.
        if (targetRatio > srcRatio) {
            // wider target — extend horizontally
            const targetW = Math.round(srcH * targetRatio);
            const total = targetW - srcW;
            setExtendLeft(Math.floor(total / 2));
            setExtendRight(Math.ceil(total / 2));
            setExtendTop(0);
            setExtendBottom(0);
        } else {
            // taller target — extend vertically
            const targetH = Math.round(srcW / targetRatio);
            const total = targetH - srcH;
            setExtendTop(Math.floor(total / 2));
            setExtendBottom(Math.ceil(total / 2));
            setExtendLeft(0);
            setExtendRight(0);
        }
    };

    const resetExtents = () => {
        setExtendTop(0);
        setExtendRight(0);
        setExtendBottom(0);
        setExtendLeft(0);
    };

    const currentRatio = canvasW / canvasH;
    const activePreset = ['16:9', '4:3', '1:1', '3:4', '9:16'].find((r) => {
        const [pw, ph] = r.split(':').map(Number);
        return Math.abs(currentRatio - pw / ph) < 0.01;
    });

    const ASPECT_OPTIONS = [
        { value: '16:9', label: 'Landscape (16:9)' },
        { value: '4:3', label: 'Landscape (4:3)' },
        { value: '1:1', label: 'Square (1:1)' },
        { value: '3:4', label: 'Portrait (3:4)' },
        { value: '9:16', label: 'Story (9:16)' },
    ];

    return (
        <div className="flex min-h-0 flex-1 flex-col gap-2.5">
            {/* Top toolbar — aspect ratio glyph picker (same control as the recipe
                nodes) on the right, reset to its left. */}
            <div className="flex min-h-10 items-center justify-end gap-2.5">
                {(totalX > 0 || totalY > 0) && (
                    <Button type="button" variant="outline" size="sm" onClick={resetExtents}>
                        {t('CONTENT_STUDIO.EXPAND_RESET')}
                    </Button>
                )}
                <AspectRatioControl options={ASPECT_OPTIONS} value={activePreset} onChange={applyAspectPreset} />
            </div>

            {/* Canvas — grows to fill; drag an edge to extend or drag the image.
                `relative` + the exact display dimensions below are the frame the
                edge-handle offsets and the drag maths are expressed in. */}
            <div className="relative flex min-h-0 flex-1 items-center justify-center overflow-hidden rounded-xl border bg-muted p-4">
                <div
                    className="relative select-none rounded-lg border border-dashed border-border"
                    style={{
                        width: dCanvasW,
                        height: dCanvasH,
                        backgroundImage:
                            'repeating-linear-gradient(45deg, rgba(74,73,69,0.06) 0 6px, transparent 6px 12px)',
                        backgroundColor: '#FFFFFF',
                        boxShadow: '0 0 0 1px rgba(0,0,0,0.04)',
                    }}
                >
                    <div
                        onMouseDown={beginImageDrag}
                        className={cn('absolute bg-cover bg-center', canRepositionImage ? 'cursor-move active:cursor-grabbing' : 'cursor-default')}
                        style={{
                            left: dOffsetX,
                            top: dOffsetY,
                            width: dSrcW,
                            height: dSrcH,
                            backgroundImage: `url(${asset.url})`,
                            boxShadow: canRepositionImage ? '0 0 0 1px rgba(74,73,69,0.35)' : 'none',
                        }}
                    />
                    <EdgeHandle side="top" style={{ left: dOffsetX, top: dOffsetY - 14, width: dSrcW }} onMouseDown={(e) => beginDrag('top', e)} label={extendTop > 0 ? `+${extendTop}` : undefined} />
                    <EdgeHandle side="bottom" style={{ left: dOffsetX, top: dOffsetY + dSrcH - 14, width: dSrcW }} onMouseDown={(e) => beginDrag('bottom', e)} label={extendBottom > 0 ? `+${extendBottom}` : undefined} />
                    <EdgeHandle side="left" style={{ left: dOffsetX - 14, top: dOffsetY, height: dSrcH }} onMouseDown={(e) => beginDrag('left', e)} label={extendLeft > 0 ? `+${extendLeft}` : undefined} />
                    <EdgeHandle side="right" style={{ left: dOffsetX + dSrcW - 14, top: dOffsetY, height: dSrcH }} onMouseDown={(e) => beginDrag('right', e)} label={extendRight > 0 ? `+${extendRight}` : undefined} />
                </div>
            </div>

            {/* Optional context — full-width single line. */}
            <Input
                value={promptText}
                onChange={(e) => setPromptText(e.target.value)}
                placeholder={t('CONTENT_STUDIO.EXPAND_PROMPT_PLACEHOLDER')}
                className="h-9"
            />

            {/* Hint */}
            <div className="text-center text-xs text-muted-foreground">
                {t('CONTENT_STUDIO.EXPAND_DRAG_HINT')}
            </div>
        </div>
    );
};

interface EdgeHandleProps {
    side: DragSide;
    style: React.CSSProperties;
    label?: string;
    onMouseDown: (e: React.MouseEvent) => void;
}

// Chunky, always-visible edge handle: a full-length guide line for the drag
// hitbox + a centered arrow pill that advertises the drag affordance.
const EdgeHandle: React.FC<EdgeHandleProps> = ({ side, style, label, onMouseDown }) => {
    const isH = side === 'top' || side === 'bottom';
    const Arrow =
        side === 'top' ? ArrowUp
        : side === 'bottom' ? ArrowDown
        : side === 'left' ? ArrowLeft
        : ArrowRight;
    return (
        <motion.div
            onMouseDown={onMouseDown}
            whileHover={{ scale: 1.08 }}
            whileTap={{ scale: 0.96 }}
            style={{
                position: 'absolute',
                ...style,
                height: isH ? 28 : undefined,
                width: isH ? undefined : 28,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: isH ? 'ns-resize' : 'ew-resize',
                zIndex: 3,
            }}
        >
            {/* Guide line centered in the hit zone — spans the full edge. */}
            <div
                style={{
                    position: 'absolute',
                    left: isH ? 0 : '50%',
                    right: isH ? 0 : 'auto',
                    top: isH ? '50%' : 0,
                    bottom: isH ? 'auto' : 0,
                    width: isH ? '100%' : 3,
                    height: isH ? 3 : '100%',
                    transform: isH ? 'translateY(-50%)' : 'translateX(-50%)',
                    backgroundColor: '#4A4945',
                    opacity: 0.55,
                    borderRadius: 2,
                }}
            />
            {/* Centered arrow pill. */}
            <div
                style={{
                    position: 'relative',
                    zIndex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 4,
                    flexDirection: isH ? 'row' : 'column',
                    backgroundColor: '#FFFFFF',
                    border: '1.5px solid #4A4945',
                    borderRadius: 999,
                    padding: label ? (isH ? '3px 10px' : '8px 3px') : (isH ? '3px 7px' : '7px 3px'),
                    minWidth: isH ? 30 : 22,
                    minHeight: isH ? 22 : 30,
                    fontSize: 10,
                    fontWeight: 500,
                    color: '#4A4945',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.18)',
                    whiteSpace: 'nowrap',
                    pointerEvents: 'none',
                    lineHeight: 1,
                }}
            >
                <Arrow size={12} strokeWidth={2.4} />
                {label && <span>{label}</span>}
            </div>
        </motion.div>
    );
};

function clampToMax(value: number, max: number): number {
    return Math.max(0, Math.min(value, Math.max(0, max)));
}

// ═══════════════════════════════════════════════════════════════════════
// Retouch tool — draw rect regions over the image, each with its own
// prompt. Worker will send them sequentially through Qwen inpaint.
// ═══════════════════════════════════════════════════════════════════════

const REGION_COLORS = [
    { accent: '#3B82F6', fill: 'rgba(59,130,246,0.18)' },   // blue
    { accent: '#EF4444', fill: 'rgba(239,68,68,0.18)' },    // red
    { accent: '#10B981', fill: 'rgba(16,185,129,0.18)' },   // green
    { accent: '#F59E0B', fill: 'rgba(245,158,11,0.18)' },   // amber
    { accent: '#8B5CF6', fill: 'rgba(139,92,246,0.18)' },   // violet
    { accent: '#EC4899', fill: 'rgba(236,72,153,0.18)' },   // pink
];

const MAX_RETOUCH_REGIONS = 6;

/** Ray-casting point-in-polygon test (source coords). */
function pointInPolygon(x: number, y: number, pts: RetouchPoint[]): boolean {
    let inside = false;
    for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
        const xi = pts[i].x, yi = pts[i].y, xj = pts[j].x, yj = pts[j].y;
        if (((yi > y) !== (yj > y)) && (x < ((xj - xi) * (y - yi)) / (yj - yi) + xi)) inside = !inside;
    }
    return inside;
}

/**
 * Paint the retouch selections onto the canvas overlay each animation frame:
 * committed regions get an ai-visualizer-style glow (breathing frosted tint + a
 * gliding diagonal shine, clipped to the polygon) plus flowing marching-ants in
 * the region colour; the in-progress lasso is an OPEN polyline (it only closes on
 * release, like the visualizer's Edit mode).
 */
function drawRetouchCanvas(
    canvas: HTMLCanvasElement,
    ts: number,
    regions: RetouchRegionParams[],
    drag: RetouchDragState,
    selectedId: string | null,
    scale: number,
    dW: number,
    dH: number,
) {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    const cw = Math.round(dW * dpr), ch = Math.round(dH * dpr);
    if (canvas.width !== cw || canvas.height !== ch) {
        canvas.width = cw; canvas.height = ch;
        canvas.style.width = `${dW}px`; canvas.style.height = `${dH}px`;
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, dW, dH);
    const t = ts / 1000;
    const S = scale || 1;

    regions.forEach((r, idx) => {
        if (!r.points || r.points.length < 3) return;
        const color = REGION_COLORS[idx % REGION_COLORS.length];
        const sp = r.points.map((p) => [p.x * S, p.y * S] as [number, number]);
        const xs = sp.map((p) => p[0]), ys = sp.map((p) => p[1]);
        const minX = Math.min(...xs), maxX = Math.max(...xs), minY = Math.min(...ys), maxY = Math.max(...ys);
        const w = Math.max(1, maxX - minX), h = Math.max(1, maxY - minY);
        const isSel = r.id === selectedId;
        const trace = () => {
            ctx.moveTo(sp[0][0], sp[0][1]);
            for (let i = 1; i < sp.length; i++) ctx.lineTo(sp[i][0], sp[i][1]);
            ctx.closePath();
        };

        // Animated glow, clipped to the polygon.
        ctx.save();
        ctx.beginPath(); trace(); ctx.clip();
        const breathe = 0.12 + 0.04 * Math.sin(t * 1.4 + idx * 0.7);
        ctx.fillStyle = `rgba(255,255,255,${breathe})`;
        ctx.fillRect(minX, minY, w, h);
        const period = 2.8;
        const p = (t % period) / period;
        const travel = w + h * 0.5;
        const cx = minX - travel * 0.5 + p * (travel * 2);
        const bandW = Math.max(60, w * 0.35);
        const sheen = ctx.createLinearGradient(cx - bandW, minY, cx + bandW, minY + h * 0.5);
        sheen.addColorStop(0.0, 'rgba(255,255,255,0)');
        sheen.addColorStop(0.5, 'rgba(255,255,255,0.28)');
        sheen.addColorStop(1.0, 'rgba(255,255,255,0)');
        ctx.fillStyle = sheen;
        ctx.fillRect(minX, minY, w, h);
        ctx.restore();

        // Marching ants — dark base + coloured dashes.
        ctx.beginPath(); trace();
        ctx.setLineDash([]); ctx.strokeStyle = 'rgba(0,0,0,0.30)'; ctx.lineWidth = isSel ? 3.5 : 3; ctx.stroke();
        ctx.beginPath(); trace();
        ctx.strokeStyle = color.accent; ctx.lineWidth = isSel ? 2.25 : 1.75;
        ctx.setLineDash([5, 6]); ctx.lineDashOffset = -((t * 26) % 11); ctx.stroke();
        ctx.setLineDash([]);
    });

    // In-progress stroke — OPEN polyline (does not connect to the first point).
    if (drag.kind === 'lasso' && drag.pts.length >= 2) {
        const sp = drag.pts;
        ctx.beginPath();
        ctx.moveTo(sp[0].x, sp[0].y);
        for (let i = 1; i < sp.length; i++) ctx.lineTo(sp[i].x, sp[i].y);
        ctx.strokeStyle = 'rgba(0,0,0,0.4)'; ctx.lineWidth = 3; ctx.stroke();
        ctx.strokeStyle = 'rgba(255,255,255,0.95)'; ctx.lineWidth = 2;
        ctx.setLineDash([6, 4]); ctx.lineDashOffset = -((t * 24) % 10); ctx.stroke();
        ctx.setLineDash([]);
    }
}

interface RetouchToolProps {
    asset: CsAsset;
    srcW: number;
    srcH: number;
    regions: RetouchRegionParams[];
    setRegions: React.Dispatch<React.SetStateAction<RetouchRegionParams[]>>;
    selectedRegionId: string | null;
    setSelectedRegionId: (id: string | null) => void;
}

// Freehand lasso draw (display-space points) or move an existing region.
type RetouchDragState =
    | { kind: 'idle' }
    | { kind: 'lasso'; pts: RetouchPoint[] }
    | { kind: 'move'; id: string; startPts: RetouchPoint[]; startPx: { x: number; y: number } };

const RetouchTool: React.FC<RetouchToolProps> = ({
    asset,
    srcW,
    srcH,
    regions,
    setRegions,
    selectedRegionId,
    setSelectedRegionId,
}) => {
    const { t } = useTranslation();
    const imgRef = useRef<HTMLDivElement>(null);
    const previewMax = usePreviewMax();

    const displayScale = useMemo(
        () => Math.min(previewMax / srcW, previewMax / srcH, 1),
        [srcW, srcH, previewMax],
    );
    const dW = srcW * displayScale;
    const dH = srcH * displayScale;

    const [drag, setDrag] = useState<RetouchDragState>({ kind: 'idle' });
    const atCapacity = regions.length >= MAX_RETOUCH_REGIONS;

    const toSrc = (p: RetouchPoint): RetouchPoint => ({
        x: Math.round(p.x / (displayScale || 1)),
        y: Math.round(p.y / (displayScale || 1)),
    });
    const centroid = (pts: RetouchPoint[]): RetouchPoint => {
        const n = pts.length || 1;
        const s = pts.reduce((a, p) => ({ x: a.x + p.x, y: a.y + p.y }), { x: 0, y: 0 });
        return { x: s.x / n, y: s.y / n };
    };

    // Canvas overlay (animated glow + marching ants + in-progress polyline).
    // A rAF loop reads the latest state from refs so the animation stays smooth.
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const regionsRef = useRef(regions); regionsRef.current = regions;
    const dragRef = useRef(drag); dragRef.current = drag;
    const selRef = useRef(selectedRegionId); selRef.current = selectedRegionId;
    const scaleRef = useRef(displayScale); scaleRef.current = displayScale;
    const dimsRef = useRef({ dW, dH }); dimsRef.current = { dW, dH };
    React.useEffect(() => {
        let raf = 0;
        const loop = (ts: number) => {
            const canvas = canvasRef.current;
            if (canvas) {
                drawRetouchCanvas(canvas, ts, regionsRef.current, dragRef.current, selRef.current, scaleRef.current, dimsRef.current.dW, dimsRef.current.dH);
            }
            raf = requestAnimationFrame(loop);
        };
        raf = requestAnimationFrame(loop);
        return () => cancelAnimationFrame(raf);
    }, []);

    // Window-level listeners so a gesture survives dragging out of bounds.
    React.useEffect(() => {
        if (drag.kind === 'idle') return;
        const el = imgRef.current;
        if (!el) return;
        const origin = el.getBoundingClientRect();
        const clampDisp = (e: MouseEvent): RetouchPoint => ({
            x: Math.max(0, Math.min(dW, e.clientX - origin.left)),
            y: Math.max(0, Math.min(dH, e.clientY - origin.top)),
        });

        const onMove = (e: MouseEvent) => {
            if (drag.kind === 'lasso') {
                if (drag.pts.length >= 160) return; // keep the polygon light
                const p = clampDisp(e);
                const last = drag.pts[drag.pts.length - 1];
                // Only append once the pointer has travelled a few px, so the
                // trace stays smooth without exploding the point count.
                if (!last || Math.hypot(p.x - last.x, p.y - last.y) >= 4) {
                    setDrag({ kind: 'lasso', pts: [...drag.pts, p] });
                }
            } else if (drag.kind === 'move') {
                const dx = (e.clientX - drag.startPx.x) / (displayScale || 1);
                const dy = (e.clientY - drag.startPx.y) / (displayScale || 1);
                // Clamp the translation so the whole polygon stays in bounds.
                let minDx = -Infinity, maxDx = Infinity, minDy = -Infinity, maxDy = Infinity;
                for (const p of drag.startPts) {
                    minDx = Math.max(minDx, -p.x); maxDx = Math.min(maxDx, srcW - p.x);
                    minDy = Math.max(minDy, -p.y); maxDy = Math.min(maxDy, srcH - p.y);
                }
                const cdx = Math.max(minDx, Math.min(maxDx, dx));
                const cdy = Math.max(minDy, Math.min(maxDy, dy));
                setRegions((prev) => prev.map((r) => {
                    if (r.id !== drag.id) return r;
                    const pts = drag.startPts.map((p) => ({ x: Math.round(p.x + cdx), y: Math.round(p.y + cdy) }));
                    return { ...r, points: pts, ...retouchBBox(pts) };
                }));
            }
        };

        const onUp = () => {
            if (drag.kind === 'lasso') {
                const srcPts = drag.pts.map(toSrc);
                const bbox = retouchBBox(srcPts);
                // Reject tiny/accidental lassos.
                if (srcPts.length >= 3 && bbox.width > 8 && bbox.height > 8) {
                    const id = `reg-${Math.random().toString(36).slice(2, 10)}`;
                    setRegions((prev) => [...prev, { id, points: srcPts, ...bbox, prompt: '' }]);
                    setSelectedRegionId(id);
                }
            }
            setDrag({ kind: 'idle' });
        };

        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        return () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [drag, dW, dH, displayScale, srcW, srcH]);

    const beginLasso = (e: React.MouseEvent) => {
        if ((e.target as HTMLElement).closest('[data-region-hit]')) return;
        const el = imgRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const dispX = e.clientX - rect.left;
        const dispY = e.clientY - rect.top;
        // Inside an existing region → grab it to move (even at capacity).
        const sx = dispX / (displayScale || 1);
        const sy = dispY / (displayScale || 1);
        for (let i = regions.length - 1; i >= 0; i--) {
            const r = regions[i];
            if (r.points && r.points.length >= 3 && pointInPolygon(sx, sy, r.points)) {
                beginMove(e, r);
                return;
            }
        }
        if (atCapacity) return;
        setSelectedRegionId(null);
        setDrag({ kind: 'lasso', pts: [{ x: dispX, y: dispY }] });
    };

    const beginMove = (e: React.MouseEvent, r: RetouchRegionParams) => {
        e.stopPropagation();
        setSelectedRegionId(r.id);
        setDrag({ kind: 'move', id: r.id, startPts: r.points, startPx: { x: e.clientX, y: e.clientY } });
    };

    const updatePrompt = (id: string, prompt: string) =>
        setRegions((prev) => prev.map((r) => (r.id === id ? { ...r, prompt } : r)));
    const removeRegion = (id: string) => {
        setRegions((prev) => prev.filter((r) => r.id !== id));
        if (selectedRegionId === id) setSelectedRegionId(null);
    };

    return (
        <div className="flex min-h-0 flex-1 flex-col gap-2">
            {/* Full-width canvas — draw a lasso, then type the change right on it.
                imgRef's box IS the lasso coordinate origin (getBoundingClientRect),
                so its `relative` + exact dW/dH must not change. */}
            <div
                className="relative flex flex-1 items-center justify-center overflow-hidden rounded-xl border bg-muted p-4"
                style={{ minHeight: previewMax + 32 }}
            >
                <div
                    ref={imgRef}
                    onMouseDown={beginLasso}
                    className={cn('relative select-none bg-cover bg-center', atCapacity ? 'cursor-not-allowed' : 'cursor-crosshair')}
                    style={{
                        width: dW,
                        height: dH,
                        backgroundImage: `url(${asset.url})`,
                        boxShadow: '0 0 0 1px rgba(0,0,0,0.04)',
                    }}
                >
                    {/* Animated canvas overlay — glow + marching ants + in-progress
                        polyline. Pointer events pass through to the image so drawing
                        / hit-testing happens on the parent (beginLasso). */}
                    <canvas
                        ref={canvasRef}
                        className="pointer-events-none absolute top-0 left-0"
                        style={{ width: dW, height: dH }}
                    />

                    {/* Numbered pins + inline comments. */}
                    {regions.map((r, idx) => {
                        const color = REGION_COLORS[idx % REGION_COLORS.length];
                        const c = centroid(r.points);
                        const isSel = selectedRegionId === r.id;
                        const anchorX = Math.min(Math.max(0, r.x * displayScale), Math.max(0, dW - 224));
                        const anchorY = r.y * displayScale;
                        return (
                            <React.Fragment key={r.id}>
                                {/* Pin at the polygon centroid. The region colour is
                                    the user's data key — it stays an inline value. */}
                                <div
                                    data-region-hit
                                    onMouseDown={(e) => beginMove(e, r)}
                                    onClick={(e) => { e.stopPropagation(); setSelectedRegionId(r.id); }}
                                    className="pointer-events-auto absolute flex size-[22px] cursor-move items-center justify-center rounded-full text-[11px] font-medium text-white"
                                    style={{
                                        left: c.x * displayScale - 11,
                                        top: c.y * displayScale - 11,
                                        backgroundColor: color.accent,
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
                                    }}
                                >
                                    {idx + 1}
                                </div>

                                {/* Comment — editable field when selected, else a compact
                                    read-only label (only if it has text). */}
                                {isSel ? (
                                    <div
                                        data-region-hit
                                        onMouseDown={(e) => e.stopPropagation()}
                                        className="pointer-events-auto absolute z-[3] flex w-56 items-center gap-1 rounded-lg border bg-background p-1.5 shadow-sm"
                                        style={{
                                            left: anchorX,
                                            top: Math.max(0, anchorY - 50),
                                            borderColor: color.accent,
                                        }}
                                    >
                                        <Input
                                            autoFocus
                                            value={r.prompt}
                                            onChange={(e) => updatePrompt(r.id, e.target.value)}
                                            onKeyDown={(e) => {
                                                // Enter confirms the comment → collapse the bubble to
                                                // its read-only label (and don't submit the dialog).
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    (e.currentTarget as HTMLInputElement).blur();
                                                    setSelectedRegionId(null);
                                                }
                                            }}
                                            placeholder={t('CONTENT_STUDIO.RETOUCH_REGION_PLACEHOLDER')}
                                            className="h-7 min-w-0 flex-1"
                                        />
                                        <Button
                                            type="button"
                                            size="icon-xs"
                                            variant="ghost"
                                            onClick={(e) => { e.stopPropagation(); removeRegion(r.id); }}
                                            aria-label={t('CONTENT_STUDIO.ACTION_REMOVE')}
                                            className="shrink-0 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                                        >
                                            <X />
                                        </Button>
                                    </div>
                                ) : r.prompt.trim() ? (
                                    <div
                                        data-region-hit
                                        onClick={(e) => { e.stopPropagation(); setSelectedRegionId(r.id); }}
                                        className="pointer-events-auto absolute z-[2] max-w-[200px] cursor-pointer overflow-hidden px-1.5 py-0.5 text-xs text-ellipsis whitespace-nowrap text-white"
                                        style={{
                                            left: anchorX,
                                            top: Math.max(0, anchorY - 26),
                                            backgroundColor: 'rgba(17,24,39,0.82)',
                                        }}
                                    >
                                        {r.prompt}
                                    </div>
                                ) : null}
                            </React.Fragment>
                        );
                    })}
                </div>
            </div>

            {/* Hint / capacity line under the canvas. */}
            <div className="flex min-h-5 items-center justify-between px-1">
                <div className="text-xs text-muted-foreground">
                    {t('CONTENT_STUDIO.RETOUCH_DRAG_HINT')}
                </div>
                {atCapacity && (
                    <div className="text-xs italic text-muted-foreground">
                        {t('CONTENT_STUDIO.RETOUCH_MAX_REGIONS', { count: MAX_RETOUCH_REGIONS })}
                    </div>
                )}
            </div>
        </div>
    );
};


// ═══════════════════════════════════════════════════════════════════════
// Modify tool — image preview + freeform prompt. No canvas manipulation;
// the worker runs Flux Klein edit against the parent asset with the
// user's prompt and a preservation footer.
// ═══════════════════════════════════════════════════════════════════════

interface ModifyToolProps {
    asset: CsAsset;
    prompt: string;
    setPrompt: (v: string) => void;
    referenceUrl?: string;
    setReferenceUrl: (url: string | undefined) => void;
    onUploadReference?: (file: File) => Promise<string>;
}

const ModifyTool: React.FC<ModifyToolProps> = ({
    asset,
    prompt,
    setPrompt,
    referenceUrl,
    setReferenceUrl,
    onUploadReference,
}) => {
    const { t } = useTranslation();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);

    const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file || !onUploadReference) return;
        setUploading(true);
        setUploadError(null);
        try {
            const url = await onUploadReference(file);
            setReferenceUrl(url);
        } catch (err: any) {
            setUploadError(err?.message || 'Upload failed');
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    return (
        <div className="flex min-h-0 flex-1 items-stretch gap-4">
            {/* Left: preview */}
            <div className="flex min-w-0 flex-1 flex-col">
                <div
                    className="min-h-[360px] w-full flex-1 rounded-lg border bg-muted bg-contain bg-center bg-no-repeat"
                    style={{ backgroundImage: `url(${asset.url})` }}
                />
            </div>

            {/* Right rail: configuration controls */}
            <div className="flex w-80 shrink-0 flex-col gap-4 overflow-auto pe-1">
                <div className="flex flex-col gap-1.5">
                    <div className="text-xs font-medium text-foreground">
                        {t('CONTENT_STUDIO.MODIFY_WHAT_SHOULD_CHANGE')}
                    </div>
                    <div className="text-xs text-muted-foreground">
                        {t('CONTENT_STUDIO.MODIFY_DESCRIBE_HELP')}
                    </div>
                    <Textarea
                        value={prompt}
                        onChange={(e) => setPrompt(e.target.value)}
                        placeholder={t('CONTENT_STUDIO.MODIFY_PROMPT_PLACEHOLDER')}
                        rows={4}
                        className="resize-y"
                    />
                </div>

                {/* Optional reference image — helps Flux match a specific visual
                  * target ("add a side table like this") rather than invent one
                  * from scratch. Ships to the worker as image 2 alongside the
                  * parent generation at image 1. */}
                <div className="flex flex-col gap-1.5">
                    <div className="flex items-baseline gap-2">
                        <div className="text-xs font-medium text-foreground">
                            {t('CONTENT_STUDIO.MODIFY_REFERENCE_IMAGE')}
                        </div>
                        <div className="text-xs text-muted-foreground">
                            {t('CONTENT_STUDIO.MODIFY_REFERENCE_HINT')}
                        </div>
                    </div>

                    <ReferenceImageSlot
                        url={referenceUrl}
                        onClear={() => setReferenceUrl(undefined)}
                        onPick={() => fileInputRef.current?.click()}
                        uploading={uploading}
                        enabled={!!onUploadReference}
                        className="w-40"
                    />
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={handleUpload}
                    />
                    {uploadError && <div className="text-xs text-destructive">{uploadError}</div>}
                </div>
            </div>
        </div>
    );
};

// ═══════════════════════════════════════════════════════════════════════
// Graphics tool — place titles, captions, and arrows on the image and
// let GPT-Image-2 render a polished marketing graphic using these as a
// layout sketch (plus an optional style reference).
// ═══════════════════════════════════════════════════════════════════════

type GraphicsMode = 'select' | 'text' | 'arrow';

// Source-pixel font sizes per preset. Scaled by displayScale for preview.
const GRAPHICS_FONT_PX: Record<GraphicsTextAnnotation['fontSize'], number> = {
    sm: 18,
    md: 28,
    lg: 44,
    xl: 68,
};

const MAX_GRAPHICS_TEXTS = 8;
const MAX_GRAPHICS_ARROWS = 8;

interface GraphicsToolProps {
    asset: CsAsset;
    texts: GraphicsTextAnnotation[];
    setTexts: React.Dispatch<React.SetStateAction<GraphicsTextAnnotation[]>>;
    arrows: GraphicsArrowAnnotation[];
    setArrows: React.Dispatch<React.SetStateAction<GraphicsArrowAnnotation[]>>;
    referenceUrl?: string;
    setReferenceUrl: (url: string | undefined) => void;
    prompt: string;
    setPrompt: (v: string) => void;
    onUploadReference?: (file: File) => Promise<string>;
}

type GraphicsDragState =
    | { kind: 'idle' }
    | { kind: 'draw-arrow'; startX: number; startY: number; curX: number; curY: number }
    | { kind: 'move-text'; id: string; startPos: { x: number; y: number }; startPx: { x: number; y: number } };

const GraphicsTool: React.FC<GraphicsToolProps> = ({
    asset,
    texts,
    setTexts,
    arrows,
    setArrows,
    referenceUrl,
    setReferenceUrl,
    prompt,
    setPrompt,
    onUploadReference,
}) => {
    const { t } = useTranslation();
    const canvasRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const previewMax = usePreviewMax();

    const srcW = asset.dimensions?.width || 1024;
    const srcH = asset.dimensions?.height || 1024;
    const displayScale = useMemo(() => {
        return Math.min(previewMax / srcW, previewMax / srcH, 1);
    }, [srcW, srcH, previewMax]);
    const dW = srcW * displayScale;
    const dH = srcH * displayScale;

    const [mode, setMode] = useState<GraphicsMode>('select');
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [drag, setDrag] = useState<GraphicsDragState>({ kind: 'idle' });
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);

    const atTextCapacity = texts.length >= MAX_GRAPHICS_TEXTS;
    const atArrowCapacity = arrows.length >= MAX_GRAPHICS_ARROWS;

    // Window-level drag listeners — survive the cursor leaving the canvas.
    React.useEffect(() => {
        if (drag.kind === 'idle') return;
        const el = canvasRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const toDisplay = (e: MouseEvent) => ({
            x: Math.max(0, Math.min(dW, e.clientX - rect.left)),
            y: Math.max(0, Math.min(dH, e.clientY - rect.top)),
        });

        const onMove = (e: MouseEvent) => {
            if (drag.kind === 'draw-arrow') {
                const p = toDisplay(e);
                setDrag({ ...drag, curX: p.x, curY: p.y });
            } else if (drag.kind === 'move-text') {
                const dx = (e.clientX - drag.startPx.x) / (displayScale || 1);
                const dy = (e.clientY - drag.startPx.y) / (displayScale || 1);
                setTexts((prev) => prev.map((a) => {
                    if (a.id !== drag.id) return a;
                    const nextX = Math.max(0, Math.min(srcW, drag.startPos.x + dx));
                    const nextY = Math.max(0, Math.min(srcH, drag.startPos.y + dy));
                    return { ...a, x: Math.round(nextX), y: Math.round(nextY) };
                }));
            }
        };
        const onUp = () => {
            if (drag.kind === 'draw-arrow') {
                const dxPx = Math.abs(drag.curX - drag.startX);
                const dyPx = Math.abs(drag.curY - drag.startY);
                if (dxPx + dyPx > 16 && !atArrowCapacity) {
                    const id = `arr-${Math.random().toString(36).slice(2, 10)}`;
                    setArrows((prev) => [
                        ...prev,
                        {
                            id,
                            x1: Math.round(drag.startX / (displayScale || 1)),
                            y1: Math.round(drag.startY / (displayScale || 1)),
                            x2: Math.round(drag.curX / (displayScale || 1)),
                            y2: Math.round(drag.curY / (displayScale || 1)),
                        },
                    ]);
                    setSelectedId(id);
                }
                setMode('select');
            }
            setDrag({ kind: 'idle' });
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        return () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
        };
    }, [drag, dW, dH, displayScale, srcW, srcH, setTexts, setArrows, atArrowCapacity]);

    const handleCanvasMouseDown = (e: React.MouseEvent) => {
        // Click on an annotation bubbles up via stopPropagation below —
        // reaching here means an empty-canvas click.
        const el = canvasRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        if (mode === 'text') {
            if (atTextCapacity) return;
            const id = `txt-${Math.random().toString(36).slice(2, 10)}`;
            setTexts((prev) => [
                ...prev,
                {
                    id,
                    text: t('CONTENT_STUDIO.GRAPHICS_DEFAULT_TEXT'),
                    x: Math.round(x / (displayScale || 1)),
                    y: Math.round(y / (displayScale || 1)),
                    fontSize: 'md',
                    weight: 'bold',
                },
            ]);
            setSelectedId(id);
            setMode('select');
        } else if (mode === 'arrow') {
            if (atArrowCapacity) return;
            setDrag({ kind: 'draw-arrow', startX: x, startY: y, curX: x, curY: y });
        } else {
            setSelectedId(null);
        }
    };

    const beginTextMove = (e: React.MouseEvent, textId: string) => {
        e.stopPropagation();
        const ann = texts.find((a) => a.id === textId);
        if (!ann) return;
        setSelectedId(textId);
        setDrag({
            kind: 'move-text',
            id: textId,
            startPos: { x: ann.x, y: ann.y },
            startPx: { x: e.clientX, y: e.clientY },
        });
    };

    const updateTextField = (id: string, patch: Partial<GraphicsTextAnnotation>) => {
        setTexts((prev) => prev.map((a) => (a.id === id ? { ...a, ...patch } : a)));
    };
    const removeText = (id: string) => {
        setTexts((prev) => prev.filter((a) => a.id !== id));
        if (selectedId === id) setSelectedId(null);
    };
    const removeArrow = (id: string) => {
        setArrows((prev) => prev.filter((a) => a.id !== id));
        if (selectedId === id) setSelectedId(null);
    };

    const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file || !onUploadReference) return;
        setUploading(true);
        setUploadError(null);
        try {
            const url = await onUploadReference(file);
            setReferenceUrl(url);
        } catch (err: any) {
            setUploadError(err?.message || 'Upload failed');
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const canvasCursor =
        mode === 'text' ? (atTextCapacity ? 'not-allowed' : 'copy')
        : mode === 'arrow' ? (atArrowCapacity ? 'not-allowed' : 'crosshair')
        : 'default';

    const selectedText = texts.find((a) => a.id === selectedId);

    return (
        <div className="flex min-h-0 flex-1 items-stretch gap-4">
            {/* Left: mode toolbar + canvas + empty hint */}
            <div className="flex min-w-0 flex-1 flex-col gap-4">
            {/* Mode toolbar */}
            <div className="flex flex-wrap items-center gap-2 rounded-lg border bg-muted p-2">
                {([
                    { id: 'select', label: t('CONTENT_STUDIO.GRAPHICS_MODE_SELECT'), Icon: MousePointer2 },
                    { id: 'text', label: t('CONTENT_STUDIO.GRAPHICS_MODE_TEXT'), Icon: TypeIcon },
                    { id: 'arrow', label: t('CONTENT_STUDIO.GRAPHICS_MODE_ARROW'), Icon: ArrowUpRight },
                ] as const).map((m) => (
                    <Button
                        key={m.id}
                        type="button"
                        variant={mode === m.id ? 'default' : 'outline'}
                        onClick={() => setMode(m.id as GraphicsMode)}
                    >
                        <m.Icon />
                        {m.label}
                    </Button>
                ))}
                <div className="flex-1" />
                <div className="text-xs text-muted-foreground">
                    {texts.length}/{MAX_GRAPHICS_TEXTS} · {arrows.length}/{MAX_GRAPHICS_ARROWS}
                </div>
            </div>

            {/* Canvas — canvasRef's box is the annotation coordinate origin
                (getBoundingClientRect); its `relative` and exact dW/dH stay. */}
            <div
                className="relative flex items-center justify-center overflow-hidden rounded-xl border bg-muted p-6"
                style={{ minHeight: previewMax + 48 }}
            >
                <div
                    ref={canvasRef}
                    onMouseDown={handleCanvasMouseDown}
                    className="relative select-none bg-cover bg-center"
                    style={{
                        width: dW,
                        height: dH,
                        backgroundImage: `url(${asset.url})`,
                        cursor: canvasCursor,
                        boxShadow: '0 0 0 1px rgba(0,0,0,0.04)',
                    }}
                >
                    {/* Arrows layer (SVG) */}
                    <svg
                        width={dW}
                        height={dH}
                        style={{ position: 'absolute', left: 0, top: 0, pointerEvents: 'none' }}
                    >
                        <defs>
                            <marker
                                id="gfx-arrowhead"
                                viewBox="0 0 10 10"
                                refX="9"
                                refY="5"
                                markerWidth="7"
                                markerHeight="7"
                                orient="auto-start-reverse"
                            >
                                <path d="M0,0 L10,5 L0,10 Z" fill="#1F2022" />
                            </marker>
                        </defs>
                        {arrows.map((a) => {
                            const isSel = selectedId === a.id;
                            return (
                                <g key={a.id} style={{ pointerEvents: 'visibleStroke' }}>
                                    <line
                                        x1={a.x1 * displayScale}
                                        y1={a.y1 * displayScale}
                                        x2={a.x2 * displayScale}
                                        y2={a.y2 * displayScale}
                                        stroke={isSel ? '#DC2626' : '#1F2022'}
                                        strokeWidth={isSel ? 4 : 3}
                                        markerEnd="url(#gfx-arrowhead)"
                                        style={{ cursor: 'pointer' }}
                                        onMouseDown={(e) => {
                                            e.stopPropagation();
                                            setSelectedId(a.id);
                                        }}
                                    />
                                </g>
                            );
                        })}
                        {drag.kind === 'draw-arrow' && (
                            <line
                                x1={drag.startX}
                                y1={drag.startY}
                                x2={drag.curX}
                                y2={drag.curY}
                                stroke="#4A4945"
                                strokeWidth={3}
                                strokeDasharray="6 4"
                                markerEnd="url(#gfx-arrowhead)"
                            />
                        )}
                    </svg>

                    {/* Text boxes layer — the annotation's own size / weight is
                        the vendor's design data, so it stays inline. */}
                    {texts.map((a) => {
                        const isSel = selectedId === a.id;
                        const sizePx = GRAPHICS_FONT_PX[a.fontSize] * displayScale;
                        return (
                            <div
                                key={a.id}
                                data-text-box
                                onMouseDown={(e) => beginTextMove(e, a.id)}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedId(a.id);
                                }}
                                className="absolute cursor-move overflow-hidden px-2 py-1 text-ellipsis whitespace-nowrap transition-colors"
                                style={{
                                    left: a.x * displayScale,
                                    top: a.y * displayScale,
                                    backgroundColor: isSel ? 'rgba(255,255,255,0.88)' : 'rgba(255,255,255,0.72)',
                                    border: `1.5px ${isSel ? 'solid' : 'dashed'} ${isSel ? '#DC2626' : '#4A4945'}`,
                                    color: '#1F2022',
                                    fontSize: sizePx,
                                    fontWeight: a.weight === 'bold' ? 700 : 500,
                                    lineHeight: 1.1,
                                    boxShadow: isSel ? '0 0 0 2px rgba(220,38,38,0.25)' : 'none',
                                    maxWidth: dW - a.x * displayScale - 8,
                                }}
                            >
                                {a.text}
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Hint when empty */}
            {texts.length === 0 && arrows.length === 0 && (
                <div className="rounded-lg border border-dashed border-border p-3 text-center text-xs italic text-muted-foreground">
                    {t('CONTENT_STUDIO.GRAPHICS_EMPTY_HINT')}
                </div>
            )}
            </div>

            {/* Right rail: selected-item editor + style config */}
            <div className="flex w-80 shrink-0 flex-col gap-4 overflow-auto pe-1">

            {/* Selected text-annotation editor */}
            {selectedText && (
                <div className="flex flex-col gap-2 rounded-lg border border-destructive/40 bg-destructive/5 p-3">
                    <div className="flex items-center gap-2 text-xs font-medium tracking-[0.04em] text-muted-foreground uppercase">
                        <span>{t('CONTENT_STUDIO.GRAPHICS_SELECTED_TEXT')}</span>
                        <div className="flex-1" />
                        <Button
                            type="button"
                            variant="outline"
                            size="xs"
                            onClick={() => removeText(selectedText.id)}
                            className="border-destructive/40 text-destructive hover:bg-destructive/5 hover:text-destructive"
                        >
                            {t('CONTENT_STUDIO.GRAPHICS_DELETE')}
                        </Button>
                    </div>
                    <Input
                        value={selectedText.text}
                        onChange={(e) => updateTextField(selectedText.id, { text: e.target.value })}
                    />
                    <div className="flex flex-wrap gap-2">
                        {(['sm', 'md', 'lg', 'xl'] as const).map((sz) => (
                            <Button
                                key={sz}
                                type="button"
                                size="xs"
                                variant={selectedText.fontSize === sz ? 'default' : 'outline'}
                                onClick={() => updateTextField(selectedText.id, { fontSize: sz })}
                                className="uppercase"
                            >
                                {sz}
                            </Button>
                        ))}
                        <div className="w-3" />
                        {(['regular', 'bold'] as const).map((w) => (
                            <Button
                                key={w}
                                type="button"
                                size="xs"
                                variant={selectedText.weight === w ? 'default' : 'outline'}
                                onClick={() => updateTextField(selectedText.id, { weight: w })}
                                className="capitalize"
                            >
                                {t(`CONTENT_STUDIO.GRAPHICS_WEIGHT_${w.toUpperCase()}`)}
                            </Button>
                        ))}
                    </div>
                </div>
            )}

            {/* Selected arrow — delete only */}
            {selectedId && !selectedText && arrows.find((a) => a.id === selectedId) && (
                <div className="flex items-center gap-2 rounded-lg border border-destructive/40 bg-destructive/5 p-3">
                    <div className="text-xs font-medium text-foreground">
                        {t('CONTENT_STUDIO.GRAPHICS_SELECTED_ARROW')}
                    </div>
                    <div className="flex-1" />
                    <Button
                        type="button"
                        variant="outline"
                        size="xs"
                        onClick={() => removeArrow(selectedId)}
                        className="border-destructive/40 text-destructive hover:bg-destructive/5 hover:text-destructive"
                    >
                        {t('CONTENT_STUDIO.GRAPHICS_DELETE')}
                    </Button>
                </div>
            )}

            {/* Style reference + freeform style prompt — stacked in the
                narrow rail so both get full width. */}
            <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-1.5">
                    <RailLabel>{t('CONTENT_STUDIO.GRAPHICS_STYLE_REFERENCE')}</RailLabel>
                    <ReferenceImageSlot
                        url={referenceUrl}
                        onClear={() => setReferenceUrl(undefined)}
                        onPick={() => fileInputRef.current?.click()}
                        uploading={uploading}
                        enabled={!!onUploadReference}
                        className="w-full"
                    />
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={handleUpload}
                    />
                    {uploadError && <div className="text-xs text-destructive">{uploadError}</div>}
                </div>

                <div className="flex flex-col gap-1.5">
                    <RailLabel>{t('CONTENT_STUDIO.GRAPHICS_STYLE_PROMPT')}</RailLabel>
                    <Textarea
                        value={prompt}
                        onChange={(e) => setPrompt(e.target.value)}
                        placeholder={t('CONTENT_STUDIO.GRAPHICS_STYLE_PROMPT_PLACEHOLDER')}
                        rows={4}
                        className="resize-y"
                    />
                </div>
            </div>
            </div>
        </div>
    );
};
